import React, { useState, useEffect, useContext } from "react";
import SupplierAppSurveyReadOnlyRow from "./SupplierAppSurveyReadOnlyRow";
import SupplierAppSurveyTableRow from "./SupplierAppSurveyTableRow";
import UserContext from "../contexts/UserContext";
import { useTranslation } from 'react-i18next';
import FetchUtil from "../utils/FetchUtil";
import "../css/ListUsers.css";
import { useNavigate } from "react-router-dom";

function SupplierAppSurveyTable({ key, setAppSurvey, partner, supplierCompanyId, resetMenu }) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [add, setAdd] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [error, setError] = useState("");
	const [isPartnerSurveyLoading, setIsPartnerSurveyLoading] = useState(false);
	const [isBuyerLocationLoading, setIsBuyerLocationLoading] = useState(false);
	const [isCompanySurveyLoading, setIsCompanySurveyLoading] = useState(false);
	const [isSupplierDateLoading, setIsSupplierDateLoading] = useState(false);
	const [companySurveyList, setCompanySurveyList] = useState([]);
	const [partnerAppSurveys, setPartnerAppSurveys] = useState([]);
	const [buyerLocations, setBuyerLocations] = useState([]);
	const [supplierDates, setSupplierDates] = useState([]);
	const [count, setCount] = useState(0);
	const [editableRecord, setEditableRecord] = useState(0);
	const closeText = t("page.close");
	const userCtx = useContext(UserContext);

	const initialValues = {
		partnershipKey: partner.partnershipKey,
		supplierCompanyId: supplierCompanyId,
		clientCompanyId: partner.clientCompanyId,
		appSurveyKey: 0,
		canRead: false,
		canWrite: false,
		priorityToBuyerScore: false,
	};
	const [formValues, setFormValues] = useState(initialValues);

	const resetCounter = () => {
		setCount(prevCount => prevCount + 1);
	};

	let userCompanyId = (userCtx.role !== "SiteAdmin") ? userCtx.userCompanyId : 0;
	useEffect(() => {
		fetchCompanySurveys();
		fetchPartnerAppSurveys();
		fetchBuyerLocations();
		fetchSupplierDates();
	}, []);

	const handleClose = (event) => {
		setAppSurvey(false);
		if (resetMenu) {
			resetMenu();
		}
	};

	function fetchCompanySurveys() {
		setError("");
		if (!supplierCompanyId || supplierCompanyId === 0) {
			return;
		}
		setIsCompanySurveyLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ source: "web", getAll: false, companyId: supplierCompanyId }),
		};
		try {
			FetchUtil.fetchAPI("MGetCompanySurvey", "/Service/CompanyService/GetCompanyAppSurvey", requestOptions, getCompanySurveySuccess, getCompanySurveyFail);
		} catch (error) {
			setIsCompanySurveyLoading(false);
			throw new Error(error.message);
		}
	}

	function getCompanySurveyFail(redirect) {
		setIsCompanySurveyLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.compGetAppSurveyFailed"));
	}

	function getCompanySurveySuccess(responseData) {
		let getCompanyAppSurveyResponse;
		if (responseData) {
			getCompanyAppSurveyResponse = responseData.getCompanyAppSurveyResponse;
			if (getCompanyAppSurveyResponse && getCompanyAppSurveyResponse.errorCode === "0") {
				setCompanySurveyList(getCompanyAppSurveyResponse.appSurveyList.slice());
			} else if (getCompanyAppSurveyResponse && getCompanyAppSurveyResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (getCompanyAppSurveyResponse) {
				setError(t("error.error") + " " + getCompanyAppSurveyResponse.errorMessage);
			}
		}
		setIsCompanySurveyLoading(false);
	}

	function fetchPartnerAppSurveys() {
		setError("");
		if (!initialValues.supplierCompanyId || initialValues.supplierCompanyId === 0
			|| !initialValues.clientCompanyId || initialValues.clientCompanyId === 0) {
			return;
		}
		resetCounter();
		setIsPartnerSurveyLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true, supplierKey: initialValues.supplierCompanyId,
				buyerKey: initialValues.clientCompanyId, userCompanyId: userCompanyId,
				includeInactivePartner: true
			}),
		};
		try {
			FetchUtil.fetchAPI("GetPartnerAppSurveys", "/Service/CompanyService/GetPartnerAppSurveys", requestOptions, retrievePartnerSurveySuccess, retrievePartnerSurveyFail);
		} catch (error) {
			setIsPartnerSurveyLoading(false);
			throw new Error(error.message);
		}
	}

	function retrievePartnerSurveyFail(redirect) {
		setIsPartnerSurveyLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.partnerGetAppSurveyFailed"));
	}

	function retrievePartnerSurveySuccess(responseData) {
		let getPartnerAppSurveyResponse;
		if (responseData) {
			getPartnerAppSurveyResponse = responseData.getPartnerAppSurveyResponse;
			if (getPartnerAppSurveyResponse && getPartnerAppSurveyResponse.errorCode === "0") {
				setPartnerAppSurveys(getPartnerAppSurveyResponse.appSurveyList.slice())
			} else if (getPartnerAppSurveyResponse && getPartnerAppSurveyResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (getPartnerAppSurveyResponse) {
				setError(t("error.error") + " " + getPartnerAppSurveyResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsPartnerSurveyLoading(false);
	}

	function fetchBuyerLocations() {
		setError("");
		if ( !initialValues.clientCompanyId || initialValues.clientCompanyId === 0) {
			return;
		}
		resetCounter();
		setBuyerLocations([]);
		setIsBuyerLocationLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: false,
				companyId: initialValues.clientCompanyId
			}),
		};
		try {
			FetchUtil.fetchAPI("GetWorkLocations", "/Service/CompanyService/GetWorkLocations", requestOptions, fetchBuyerLocationSuccess, fetchBuyerLocationFail);
		} catch (error) {
			setIsBuyerLocationLoading(false);
			setError(t("error.error") + " " + error.message);
		}
	}

	function fetchBuyerLocationFail() {
		setIsBuyerLocationLoading(false);
		setError(t("error.error") + " " + t("error.locNotRetrieved"));
	}

	function fetchBuyerLocationSuccess(responseData) {
		let fetchWorkLocationsResponse;
		if (responseData) {
			fetchWorkLocationsResponse = responseData.fetchWorkLocationsResponse;
			if (fetchWorkLocationsResponse && fetchWorkLocationsResponse.errorCode === "0") {
				setBuyerLocations(fetchWorkLocationsResponse.locations.slice());
			} else if (fetchWorkLocationsResponse && fetchWorkLocationsResponse.errorCode === "114"){
				navigate("/",{
			        state: {
			            	errorMessage: t("error.sessionTimeout"),
			        	},
			     });
			} else if(fetchWorkLocationsResponse){
				setError(t("error.error") + " " + fetchWorkLocationsResponse.errorMessage);
			} else{
				setError(t("error.system.fetch"));
			}
		}
		setIsBuyerLocationLoading(false);
	}
	function fetchSupplierDates() {
		setError("");
		if (!initialValues.supplierCompanyId || initialValues.supplierCompanyId === 0
			|| !initialValues.partnershipKey || initialValues.partnershipKey === 0
			|| !initialValues.clientCompanyId || initialValues.clientCompanyId === 0) {
			return;
		}
		resetCounter();
		setSupplierDates([]);
		setIsSupplierDateLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: false, supplierKey: initialValues.supplierCompanyId,
				partnershipKey: initialValues.partnershipKey, buyerKey: initialValues.clientCompanyId,
				userCompanyId: userCompanyId,
				includeInactivePartner: true
			}),
		};
		try {
			FetchUtil.fetchAPI("GetPartnerDates", "/Service/CompanyService/GetPartnerDates", requestOptions, retrievePartnerDateSuccess, retrievePartnerDateFail);
		} catch (error) {
			setIsPartnerSurveyLoading(false);
			throw new Error(error.message);
		}
	}

	function retrievePartnerDateFail(redirect) {
		setIsSupplierDateLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.partnerGetDateFailed"));
	}

	function retrievePartnerDateSuccess(responseData) {
		let getPartnerDatesResponse;
		if (responseData) {
			getPartnerDatesResponse = responseData.getPartnerDatesResponse;
			if (getPartnerDatesResponse && getPartnerDatesResponse.errorCode === "0") {
				setSupplierDates(getPartnerDatesResponse.supplierDates.slice())
			} else if (getPartnerDatesResponse && getPartnerDatesResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (getPartnerDatesResponse) {
				setError(t("error.error") + " " + getPartnerDatesResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsSupplierDateLoading(false);
	}
	const handleDeleteClick = (event, partnerSurveyItem, completeCallback) => {
		event.preventDefault();
		deletePartnerSurvey(partnerSurveyItem, completeCallback);
	};
	const deletePartnerSurvey = (partnerSurveyItem, completeCallback) => {
		let action = 3;
		if (partnerSurveyItem.statusCode === 1) {
			action = 4;
		}
		let serverParams = {
			supplierCompanyId: formValues.supplierCompanyId,
			clientCompanyId: formValues.clientCompanyId,
			partnershipKey: formValues.partnershipKey,
			appSurveyKey: partnerSurveyItem.appSurveyKey,
			userCompanyId: userCompanyId,
			source: "web",
			action: action
		};
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("DeletePartnerAppSurvey", "/Service/CompanyService/DeletePartnerAppSurvey", requestOptions, deletePartnerAppSurveySuccess, deletePartnerAppSurveyFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function deletePartnerAppSurveyFail(redirect, completeCallback) {
		setError(t("error.partnerAppSurveyEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function deletePartnerAppSurveySuccess(responseData, completeCallback) {
		let deletePartnerAppSurveyResponse;
		if (responseData) {
			deletePartnerAppSurveyResponse = responseData.deletePartnerAppSurveyResponse;
			if (deletePartnerAppSurveyResponse) {
				if (deletePartnerAppSurveyResponse.errorCode === "0") {
					setAdd(false);
					fetchPartnerAppSurveys();
				} else {
					setError(t("error.error") + " " +
						deletePartnerAppSurveyResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	const addRowTable = () => {
		setError("");
		if(isEditMode){
			setError(t("error.onlyOneOpAllowed"));
		} else {
			if (!add) {
				setAdd(true);
			} else {
				setError(t("error.only1RowAllowed"));
			}
		}
	};
	const removeRowTable = () => {
		setError("");
		setAdd(false);
	};
	const handleSubmit = (action, setValues, completeCallback) => {
		setError("");
		let found = false;
		if (!formValues.supplierCompanyId || formValues.supplierCompanyId === 0
			|| !formValues.clientCompanyId || formValues.clientCompanyId === 0) {
			setError(t("error.partnerAppSurveyEditFailed"));
			if (completeCallback) {
				completeCallback();
			}
			return false;
		}
		if (action === 1) {
			if (partnerAppSurveys.length > 0) {
				partnerAppSurveys.map((partnerSurveyItem, index) => {
					if (partnerSurveyItem.appSurveyKey === setValues.appSurveyKey) {
						setError(t("error.partnerAppSurveyDup"));
						found = true;
						if (completeCallback) {
							completeCallback();
						}
						return false;
					}
				})
			}
			if (!found) {
				savePartnerAppSurvey(action, setValues, completeCallback);
			} else {
				if (completeCallback) {
					completeCallback();
				}
			}
		} else if (action === 2) {
			if (partnerAppSurveys.length > 0) {
				partnerAppSurveys.map((partnerSurveyItem, index) => {
					if (partnerSurveyItem.appSurveyKey === setValues.appSurveyKey) {
						found = true;
					}
				})
			}
			if (found) {
				savePartnerAppSurvey(action, setValues, completeCallback);
			} else {
				if (completeCallback) {
					completeCallback();
				}
			}
		} else if (completeCallback) {
			completeCallback();
		}
	};
	const savePartnerAppSurvey = (action, setValues, completeCallback) => {
		let serverParams = {
			supplierCompanyId: formValues.supplierCompanyId,
			clientCompanyId: formValues.clientCompanyId,
			partnershipKey: formValues.partnershipKey,
			userCompanyId: userCompanyId,
			appSurveyKey: setValues.appSurveyKey,
			canRead: setValues.canRead,
			canWrite: setValues.canWrite,
			priorityToBuyerScore: setValues.priorityToBuyerScore,
			locationKeys: setValues.locationKeys,
			partnershipDateKeys: setValues.partnershipDateKeys,
			source: "web",
			action: action
		};
		//alert(JSON.stringify(serverParams));
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("AddUpdatePartnerAppSurvey", "/Service/CompanyService/AddUpdatePartnerAppSurvey", requestOptions, savePartnerAppSurveySuccess, savePartnerAppSurveyFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function savePartnerAppSurveyFail(redirect, completeCallback) {
		setError(t("error.partnerAppSurveyEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function savePartnerAppSurveySuccess(responseData, completeCallback) {
		let addUpdatePartnerAppSurveyResponse;
		if (responseData) {
			addUpdatePartnerAppSurveyResponse = responseData.addUpdatePartnerAppSurveyResponse;
			if (addUpdatePartnerAppSurveyResponse) {
				if (addUpdatePartnerAppSurveyResponse.errorCode === "0") {
					setAdd(false);
					fetchPartnerAppSurveys();
				} else {
					setError(t("error.error") + " " +
						addUpdatePartnerAppSurveyResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	return (
		<>
			{(userCtx.role === "SiteAdmin" || userCtx.role === "ClientAdmin") ?
				<div className="container-fluid container_list_sm viewh-85">
					<div className="row">
						<div className="col mt-3">
							{(error) ? (
								<div id="inerrorMessage" className="errorblock">
									{error}
								</div>
							) : (<></>)}
						</div>
						{(isPartnerSurveyLoading || isCompanySurveyLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
					</div>
					<div className="row" style={{ margin: "0px" }}>
						<div className="col">
							<a class="popup-close" id="boxclose" onClick={(e) => handleClose(e)} ></a>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<div id="placeholder_1" className="placeholder-pg"></div>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<table className="list-table" role="table">
								<thead>
									<tr role="row">
										<th className="list-th" style={{ maxWidth: "10vw" }}>{t("page.survey.name")}</th>
										<th className="list-th" style={{ maxWidth: "25vw" }}>{t("page.loc.buyer")}</th>
										<th className="list-th" style={{ maxWidth: "30vw" }}>{t("page.dates.access")}</th>
										<th className="list-th" style={{ maxWidth: "5vw" }}>{t("page.read.sup")}</th>
										<th className="list-th" style={{ maxWidth: "5vw" }}>{t("page.write.buy")}</th>
										<th className="list-th" style={{ maxWidth: "5vw" }}>{t("page.priority.buyer.score")}</th>
										<th className="list-th" style={{ maxWidth: "5vw" }}>{t("page.status")}</th>
										<th className="list-th" style={{ maxWidth: "15vw" }}>
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={addRowTable}>
												{t("page.addRow")}
											</button>&nbsp;&nbsp;
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={removeRowTable}>
												{t("page.delRow")}
											</button>
										</th>
									</tr>
								</thead>
								<tbody role="rowgroup">
									{add &&
										<SupplierAppSurveyTableRow
											index={count}
											companySurveyList={companySurveyList}
											buyerLocations={buyerLocations}
											supplierDates={supplierDates}
											handleSubmit={handleSubmit}
											setError={setError}
											t={t}
											setAdd={setAdd}
											oddIdx={((partnerAppSurveys.length % 2) > 0) ? true : false} />
									}
									{(partnerAppSurveys.length > 0) ? (
										partnerAppSurveys.map((partnerSurveyItem, index) => (
											<SupplierAppSurveyReadOnlyRow
												index={count}
												t={t}
												partnerSurveyItem={partnerSurveyItem}
												buyerLocations={buyerLocations}
												supplierDates={supplierDates}
												handleDeleteClick={handleDeleteClick}
												oddIdx={((index % 2) > 0) ? true : false}
												add={add}
												setIsEditMode={setIsEditMode}
												isEditMode={editableRecord == index? isEditMode: false}
												setError={setError}
												handleSubmit={handleSubmit}
												setEditableRecord={setEditableRecord}
												rowCount={index}
											/>
										))
									) : (<></>)}
								</tbody>
							</table>
							<div className="row">
								<div className="col mt-3">
									{(error) ? (
										<div id="inerrorMessage1" className="errorblock">
											{error}
										</div>
									) : (<></>)}
								</div>
							</div>
							<div className="footer">
								<input
									type="button"
									name="edit-comp-appsurvey-close"
									id="edit-comp-appsurvey-close"
									className="btn btn-secondary btn-block" data-dismiss="modal"
									onClick={(e) => handleClose(e)}
									style={{ marginTop: "10px", marginBottom: "10px" }}
									value={closeText}
								></input>
							</div>
						</div>
					</div>
				</div>
				: null}
		</>
	);
}
export default SupplierAppSurveyTable;